import React, { FC, useCallback, useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { HelpBubble } from './help-bubble';
import { Icon } from '@packages/ui/shared';
import { PageDetail } from '@web/vin-sessions/types';
import { TablePagination } from '@web/components/table';
import { useAuthState } from '@packages/contexts/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePublicationsState } from '../stis-publications/context/publications.hooks';
import { useRef } from 'react';
import colors from '@packages/core/styles/colors';
import useQuery from '@web/core/hooks/use-query';

type DocsPaginationProps = {
    setParams: (params: string) => void;
    pageDetail: PageDetail;
    feedbackContext: {
        query: string;
        publicationTypeIds: string[];
        modelCodeId: string;
        modelYear: string;
        carlineId: string;
        searchType: string;
        docStatus?: string;
    };
    searchType?: string;
    /** Recently Viewed never has more than one page, so it hides the pager and keeps the help icon. */
    hidePagination?: boolean;
};

const useStyles = createUseStyles({
    paginationContainer: {
        height: 64,
        bottom: 0,
        borderTop: `1px solid ${colors.grayThree}`,
        paddingLeft: 20,
        marginBottom: 10,
        position: 'relative',
    },
    icon: {
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 36,
        width: 36,
        backgroundColor: colors.blueOne,
        right: '48px',
        bottom: '40px',
        borderRadius: 4,
        cursor: 'pointer',
    },
});

const DocsPagination: FC<DocsPaginationProps> = (props) => {
    const { setParams, pageDetail, feedbackContext, searchType, hidePagination } = props;
    const classes = useStyles();
    const { account } = useAuthState();
    const navigate = useNavigate();
    const { pathname, search } = useLocation();
    const queryParams = useQuery();
    const query = queryParams.get('query') || '';
    const pageFromQuery = queryParams.get('page') || '0';
    const sizeFromQuery = queryParams.get('size') || '10';
    const parsedPage = pageFromQuery ? parseInt(pageFromQuery, 10) : 0;
    const parsedSize = sizeFromQuery ? parseInt(sizeFromQuery, 10) : 10;
    const [startCount, setStartCount] = useState('0');
    const [endCount, setEndCount] = useState('0');
    const [docsTotal, setDocsTotal] = useState('0');
    const [showHelpBubble, setShowHelpBubble] = useState(false);
    const helpIconRef = useRef<HTMLDivElement>(null);
    const { stisFeedbackPrompt } = usePublicationsState();

    useEffect(() => {
        if (stisFeedbackPrompt === false && showHelpBubble === false && query.length > 0) {
            const timer = setTimeout(() => {
                setShowHelpBubble(true);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [stisFeedbackPrompt, query]);

    useEffect(() => {
        setStartCount(pageDetail?.totalElements === 0 ? '0' : (pageDetail?.number * pageDetail?.size + 1).toString());
        setEndCount(Math.min((pageDetail?.number + 1) * pageDetail?.size, pageDetail?.totalElements).toString());
        setDocsTotal(pageDetail?.totalElements.toString());
    }, [pageDetail]);

    useEffect(() => {
        let params = new URLSearchParams();
        if (searchType && searchType.length > 0) {
            params.set('filter', searchType);
        }
        if (queryParams.has('query')) {
            params.set('query', queryParams.get('query') || '');
        }
        params.set('page', '' + pageFromQuery);
        params.set('size', '' + sizeFromQuery);
        const sort = queryParams.get('sort');
        const sortDir = queryParams.get('sortDir');
        setParams(
            params.toString() +
                (sort && sort.length > 0 && sortDir && sortDir.length > 0
                    ? `&sort=${sort},${sortDir.toLowerCase()}`
                    : '')
        );
    }, [searchType, queryParams, pageFromQuery, sizeFromQuery]);

    const handlePaginationClick = useCallback(
        (index: number) => {
            const params = new URLSearchParams(search);
            params.set('page', String(index));
            params.set('size', String(sizeFromQuery));
            navigate(`${pathname}?${params.toString()}`);
        },
        [navigate, pathname, search, sizeFromQuery]
    );

    const handlePaginationSizeChange = useCallback(
        (size: number) => {
            const params = new URLSearchParams(search);

            params.set('page', String(0));
            params.set('size', String(size));

            navigate(`${pathname}?${params.toString()}`);
        },
        [navigate, pathname, search, pageFromQuery]
    );

    const handleHelpClick = () => {
        if (helpIconRef.current) {
            setShowHelpBubble((prev) => !prev);
        }
    };

    return (
        <>
            <div className={classes.paginationContainer}>
                {!hidePagination && (
                    <TablePagination
                        page={parsedPage}
                        size={parsedSize}
                        total={parseInt(docsTotal, 10)}
                        onClick={handlePaginationClick}
                        onSizeChange={handlePaginationSizeChange}
                        displayCount={`${startCount}-${endCount} of ${docsTotal}`}
                    />
                )}
                {account && (
                    <div ref={helpIconRef} className={classes.icon} onClick={handleHelpClick}>
                        <Icon name="question-mark" color="white" size={24} />
                    </div>
                )}
            </div>
            {showHelpBubble && helpIconRef.current && (
                <HelpBubble onClose={() => setShowHelpBubble(false)} feedbackContext={feedbackContext} />
            )}
        </>
    );
};

export default DocsPagination;
